import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';

const Careers = () => {
  const [form, setForm] = useState({
    title: '',
    companyName: '',
    description: '',
    shortDescription: '',
    experience: 'Fresher',
    education: "Bachelor's",
    jobType: 'Full Time',
    city: '',
    state: '',
    country: 'India',
    applicationDeadline: '',
    category: 'Software Development',
  });

  const [skillsInput, setSkillsInput] = useState('');
  const [skills, setSkills] = useState([]);
  const [preferredSkillsInput, setPreferredSkillsInput] = useState('');
  const [preferredSkills, setPreferredSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Update/Delete by title state
  const [updateTitle, setUpdateTitle] = useState('');
  const [updateFields, setUpdateFields] = useState({
    shortDescription: '',
    jobType: 'Full Time',
    city: '',
    state: '',
    country: 'India',
    status: 'Active',
    salaryMin: '',
    salaryMax: '',
    salaryCurrency: 'INR',
    salaryPeriod: 'Annual',
    salaryIsNegotiable: false,
    salaryDisplayText: '',
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteTitle, setDeleteTitle] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const apiBase = API_BASE_URL;
  const { user } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const addTokenized = (input, setInput, list, setList) => {
    const tokens = input
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    if (tokens.length === 0) return;
    const next = Array.from(new Set([...list, ...tokens]));
    setList(next);
    setInput('');
  };

  const removeFromList = (idx, list, setList) => {
    setList(list.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        title: form.title,
        company: {
          name: form.companyName,
        },
        description: form.description,
        shortDescription: form.shortDescription,
        requirements: {
          experience: form.experience,
          education: form.education,
          skills: skills,
          preferredSkills: preferredSkills,
        },
        jobType: form.jobType,
        location: {
          city: form.city,
          state: form.state,
          country: form.country || 'India',
          isRemote: false,
        },
        salary: {
          displayText: 'As per industry standards',
        },
        applicationDeadline: form.applicationDeadline,
        applicationProcess: 'Direct Application',
        category: form.category,
        status: 'Active',
      };

      const res = await fetch(`${apiBase}/api/careers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.message || 'Failed to create job');
      }

      setSuccess('Job posted successfully');
      // Reset basic fields, keep category defaults
      setForm((prev) => ({
        ...prev,
        title: '',
        companyName: '',
        description: '',
        shortDescription: '',
        city: '',
        state: '',
        applicationDeadline: '',
      }));
      setSkills([]);
      setPreferredSkills([]);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateByTitle = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setUpdateLoading(true);
    try {
      if (!user?.token) {
        throw new Error('You must be logged in to update a job');
      }

      // Build minimal payload: only include provided fields
      const payload = {};
      if (updateFields.shortDescription) payload.shortDescription = updateFields.shortDescription;
      if (updateFields.jobType) payload.jobType = updateFields.jobType;
      const loc = {};
      if (updateFields.city) loc.city = updateFields.city;
      if (updateFields.state) loc.state = updateFields.state;
      if (updateFields.country) loc.country = updateFields.country;
      if (Object.keys(loc).length) payload.location = { ...loc };
      if (updateFields.status) payload.status = updateFields.status;
      
      // Build salary object if any salary fields are provided
      const salary = {};
      if (updateFields.salaryMin !== '') salary.min = parseFloat(updateFields.salaryMin);
      if (updateFields.salaryMax !== '') salary.max = parseFloat(updateFields.salaryMax);
      if (updateFields.salaryCurrency) salary.currency = updateFields.salaryCurrency;
      if (updateFields.salaryPeriod) salary.period = updateFields.salaryPeriod;
      if (updateFields.salaryIsNegotiable !== undefined) salary.isNegotiable = updateFields.salaryIsNegotiable;
      if (updateFields.salaryDisplayText) salary.displayText = updateFields.salaryDisplayText;
      if (Object.keys(salary).length > 0) payload.salary = salary;

      const res = await fetch(`${apiBase}/api/careers/by-title/${encodeURIComponent(updateTitle)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.message || 'Failed to update job');
      }
      setSuccess('Job updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to update job');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteByTitle = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setDeleteLoading(true);
    try {
      if (!user?.token) {
        throw new Error('You must be logged in to delete a job');
      }
      const res = await fetch(`${apiBase}/api/careers/by-title/${encodeURIComponent(deleteTitle)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`,
        },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.message || 'Failed to delete job');
      }
      setSuccess('Job deleted successfully');
      setDeleteTitle('');
    } catch (err) {
      setError(err.message || 'Failed to delete job');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Career - Post a Job</h1>
        <p className="text-sm text-gray-600 mt-1">Fill the required fields and submit to create a job.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
            <input name="title" value={form.title} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="e.g., Frontend Developer" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
            <input name="companyName" value={form.companyName} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="e.g., Innova8r" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Short Description *</label>
            <input name="shortDescription" value={form.shortDescription} onChange={handleChange} required maxLength={200} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Brief summary (max 200 chars)" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Application Deadline *</label>
            <input type="date" name="applicationDeadline" value={form.applicationDeadline} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Experience *</label>
            <select name="experience" value={form.experience} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              {['Fresher','0-1 Years','1-2 Years','2-3 Years','3-5 Years','5+ Years','Senior Level'].map((opt)=> (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Education *</label>
            <select name="education" value={form.education} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              {["High School","Diploma","Bachelor's","Master's","PhD","Any"].map((opt)=> (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Type *</label>
            <select name="jobType" value={form.jobType} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              {['Full Time','Part Time','Contract','Internship','Freelance','Remote'].map((opt)=> (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
            <select name="category" value={form.category} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              {[
                'Software Development','Data Science & Analytics','AI/ML Engineering','DevOps & Cloud','Mobile Development','Web Development','UI/UX Design','Product Management','Quality Assurance','Cybersecurity','Robotics & IoT','Embedded Systems','Hardware Engineering','Research & Development','Technical Writing','Sales & Marketing','Operations','Other'
              ].map((opt)=> (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
            <input name="city" value={form.city} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="e.g., Pune" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
            <input name="state" value={form.state} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="e.g., Maharashtra" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
            <input name="country" value={form.country} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="e.g., India" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Description *</label>
          <textarea name="description" value={form.description} onChange={handleChange} required rows={6} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Role responsibilities, requirements, benefits..." />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Skills (comma separated)</label>
            <div className="flex gap-2">
              <input value={skillsInput} onChange={(e)=> setSkillsInput(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-md" placeholder="e.g., React, Node.js" />
              <button type="button" onClick={()=> addTokenized(skillsInput, setSkillsInput, skills, setSkills)} className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md">Add</button>
            </div>
            {skills.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {skills.map((s, idx)=> (
                  <span key={`${s}-${idx}`} className="px-2 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded">
                    {s}
                    <button type="button" className="ml-2 text-blue-700" onClick={()=> removeFromList(idx, skills, setSkills)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Skills (comma separated)</label>
            <div className="flex gap-2">
              <input value={preferredSkillsInput} onChange={(e)=> setPreferredSkillsInput(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-md" placeholder="Nice-to-haves" />
              <button type="button" onClick={()=> addTokenized(preferredSkillsInput, setPreferredSkillsInput, preferredSkills, setPreferredSkills)} className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md">Add</button>
            </div>
            {preferredSkills.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {preferredSkills.map((s, idx)=> (
                  <span key={`${s}-${idx}`} className="px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded">
                    {s}
                    <button type="button" className="ml-2 text-green-700" onClick={()=> removeFromList(idx, preferredSkills, setPreferredSkills)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="pt-2">
          <button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-60">
            {loading ? 'Posting...' : 'Post Job'}
          </button>
        </div>
      </form>

      {/* Update by Title */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Update Job by Title</h2>
        <form onSubmit={handleUpdateByTitle} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Exact Title *</label>
              <input value={updateTitle} onChange={(e)=> setUpdateTitle(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="e.g., Frontend Developer" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
              <select value={updateFields.jobType} onChange={(e)=> setUpdateFields((p)=> ({...p, jobType: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                {['Full Time','Part Time','Contract','Internship','Freelance','Remote'].map((opt)=> (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={updateFields.status} onChange={(e)=> setUpdateFields((p)=> ({...p, status: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                {['Active','Closed','Paused','Draft'].map((opt)=> (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input value={updateFields.city} onChange={(e)=> setUpdateFields((p)=> ({...p, city: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input value={updateFields.state} onChange={(e)=> setUpdateFields((p)=> ({...p, state: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input value={updateFields.country} onChange={(e)=> setUpdateFields((p)=> ({...p, country: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
            <input value={updateFields.shortDescription} onChange={(e)=> setUpdateFields((p)=> ({...p, shortDescription: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          
          {/* Salary Fields */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Salary Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Salary</label>
                <input 
                  type="number" 
                  value={updateFields.salaryMin} 
                  onChange={(e)=> setUpdateFields((p)=> ({...p, salaryMin: e.target.value}))} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md" 
                  placeholder="e.g., 50000"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Salary</label>
                <input 
                  type="number" 
                  value={updateFields.salaryMax} 
                  onChange={(e)=> setUpdateFields((p)=> ({...p, salaryMax: e.target.value}))} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md" 
                  placeholder="e.g., 100000"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select 
                  value={updateFields.salaryCurrency} 
                  onChange={(e)=> setUpdateFields((p)=> ({...p, salaryCurrency: e.target.value}))} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  {['INR', 'USD', 'EUR', 'GBP'].map((opt)=> (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                <select 
                  value={updateFields.salaryPeriod} 
                  onChange={(e)=> setUpdateFields((p)=> ({...p, salaryPeriod: e.target.value}))} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  {['Hourly', 'Monthly', 'Annual'].map((opt)=> (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Text</label>
                <input 
                  type="text" 
                  value={updateFields.salaryDisplayText} 
                  onChange={(e)=> setUpdateFields((p)=> ({...p, salaryDisplayText: e.target.value}))} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md" 
                  placeholder="e.g., As per industry standards"
                />
              </div>
              <div className="flex items-center">
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    checked={updateFields.salaryIsNegotiable} 
                    onChange={(e)=> setUpdateFields((p)=> ({...p, salaryIsNegotiable: e.target.checked}))} 
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Salary is Negotiable</span>
                </label>
              </div>
            </div>
          </div>
          
          <div className="pt-1">
            <button type="submit" disabled={updateLoading} className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md disabled:opacity-60">
              {updateLoading ? 'Updating...' : 'Update Job'}
            </button>
          </div>
        </form>
      </div>

      {/* Delete by Title */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Delete Job by Title</h2>
        <form onSubmit={handleDeleteByTitle} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Exact Title *</label>
            <input value={deleteTitle} onChange={(e)=> setDeleteTitle(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="e.g., Frontend Developer" />
          </div>
          <button type="submit" disabled={deleteLoading} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md disabled:opacity-60">
            {deleteLoading ? 'Deleting...' : 'Delete Job'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Careers;


